import { useState } from 'react';
import { useForm, formRootRule, isNotEmpty } from '@mantine/form';

import { useShowResponseStatus } from '@/shared';
import { useGetConversionPairsQuery } from '@/modules/conversion-pairs';
import { useGetRemotePaymentMethodsByCurrencyQuery } from '@/modules/remote-payment-methods';
import { useUpdatePaymentMethodMutation } from '../store/payment-methods-api';
import { handlePaymentMethodUpdateError } from '../utils/helpers/payment-methods-errors';
import {
  isPaymentMethodWithCustomFlow,
  isPaymentMethodWithRemoteMethod,
} from '../utils/helpers/payment-method-mode';

import type { TPaymentMethodUpdateParams, TPaymentMethod, TPaymentMethodData, TPaymentMethodMode } from '../store/types';

type FormData = Omit<TPaymentMethodUpdateParams, 'data' | 'remoteMethodId' | 'conversionPairId'> & {
  data: TPaymentMethodData;
  useBlockchain: boolean;
  remoteMethodId?: string;
  conversionPairId: string | null;
};

type Params = {
  paymentMethod: TPaymentMethod;
  onClose?: () => void;
};

export const useUpdatePaymentMethod = ({ paymentMethod, onClose }: Params) => {
  const [updatePaymentMethod, { isLoading, isSuccess, error }] = useUpdatePaymentMethodMutation();
  const updateError = handlePaymentMethodUpdateError(error);
  const {
    data: conversionPairs,
    isLoading: isConversionPairsLoading,
  } = useGetConversionPairsQuery();

  const initialValues: FormData = {
    methodId: paymentMethod.id,
    name: paymentMethod.name,
    refillPercent: paymentMethod.refillPercent,
    additionalPercent: paymentMethod.additionalPercent,
    paymentExpireMinutes: paymentMethod.paymentExpireMinutes,
    minRefill: paymentMethod.minRefill,
    maxRefill: paymentMethod.maxRefill,
    active: paymentMethod.active,
    useUniqueAmount: paymentMethod.useUniqueAmount,
    uniqueAmountStep: paymentMethod.uniqueAmountStep,
    mode: paymentMethod.mode,
    useBlockchain: paymentMethod.data?.blockchain?.enabled ?? Boolean(paymentMethod.data?.blockchain),
    remoteMethodId: paymentMethod.remoteMethodId?.toString() ?? '',
    conversionPairId: paymentMethod.conversionPairId?.toString() ?? null,
    data: paymentMethod.data ?? {
      step1: {
        title: '',
        options: [],
      },
      step2: {
        title: '',
        mainText: '',
      },
      step3: {
        title: '',
      },
      confirmation: {
        type: 'receipt',
        fileText: '',
        additionalText: '',
        phoneCountry: '',
      },
    },
  };

  const [useBlockchain, setUseBlockchain] = useState(initialValues.useBlockchain);
  const [methodMode, setMethodMode] = useState<TPaymentMethodMode>(paymentMethod.mode);
  const [conversionPairId, setConversionPairId] = useState(initialValues.conversionPairId);
  const [confirmationType, setConfirmationType] = useState(initialValues.data?.confirmation.type);
  const selectedConversionPair = conversionPairs?.find((pair) => pair.id === Number(conversionPairId));
  const remoteMethodsCurrency = methodMode === 'h2h'
    ? selectedConversionPair?.quoteCurrency ?? paymentMethod.currency
    : paymentMethod.currency;
  const {
    currentData: remoteMethods,
    isFetching: isRemoteMethodsLoading,
  } = useGetRemotePaymentMethodsByCurrencyQuery({ currency: remoteMethodsCurrency });
  const avalableConversionPairs = conversionPairs?.filter((pair) => (
    methodMode === 'h2h'
      ? pair.baseCurrency === paymentMethod.currency
      : pair.quoteCurrency === paymentMethod.currency
  )) ?? [];

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: { ...JSON.parse(JSON.stringify(initialValues)) },
    validate: {
      name: (value) => !value ? 'Введите название метода' : null,
      refillPercent: (value) => value !== 0 && !value ? 'Введите процент пополнения' : null,
      paymentExpireMinutes: (value) => value < 1 ? 'Введите время действия заявки' : null,
      minRefill: (value) => !value ? 'Введите минимальное пополнение' : null,
      maxRefill: (value) => !value ? 'Введите максимальное пополнение' : null,
      uniqueAmountStep: (value) => value > 0 ? null : 'Введите положительный шаг уникализации',
      mode: (value) => !value ? 'Выберите режим метода' : null,
      remoteMethodId: (value, values) => isPaymentMethodWithRemoteMethod(values.mode) && !value ? 'Выберите сторонний платёжный метод' : null,
      data: {
        blockchain: {
          addressField: (value, values) => values.useBlockchain && !value ? 'Введите название поля с адресом кошелька' : null,
          requiredConfirmations: (value, values) => values.useBlockchain && (!value || value < 1 || value > 1000) ? 'Укажите от 1 до 1000 подтверждений' : null,
        },
        step1: {
          title: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && !value ? 'Введите название выбора способа оплаты' : null,
          options: {
            [formRootRule]: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && value.length === 0 ? 'Добавьте способы оплаты' : null,
            logo: (value, values) => isPaymentMethodWithCustomFlow(values.mode) ? isNotEmpty('Введите название файла')(value) : null,
            name: (value, values) => isPaymentMethodWithCustomFlow(values.mode) ? isNotEmpty('Введите название банка или платёжной системы')(value) : null,
          }
        },
        step2: {
          title: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && !value ? 'Введите заголовок для совершения платежа' : null,
          mainText: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && !value ? 'Введите инструкции по шагам 1, 2, 3 (каждая с новой строки)' : null,
        },
        step3: {
          title: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && !value ? 'Введите заголовок для уведомления об обработке платежа' : null,
        },
        confirmation: {
          type: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && !value ? 'Выберите тип подтверждения платежа' : null,
          fileText: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && values.data?.confirmation.type === 'receipt' && !value ? 'Введите заголовок для загрузки файла' : null,
          additionalText: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && !value ? 'Введите заголовок для текстового поля' : null,
          phoneCountry: (value, values) => isPaymentMethodWithCustomFlow(values.mode) && !value ? 'Введите двубуквенный код страны' : null,
        },
      },
    },
  });

  form.watch('useBlockchain', ({ value }) => {
    setUseBlockchain(value);
    const blockchain = form.getValues().data.blockchain;
    if (value || blockchain) {
      form.setFieldValue('data.blockchain', {
        network: 'tron',
        addressField: 'Адрес',
        requiredConfirmations: 20,
        ...blockchain,
        enabled: value,
      });
    }
    if (value || blockchain) form.setFieldValue('data.confirmation.type', 'hash');
  });

  form.watch('data.confirmation.type', ({ value }) => {
    setConfirmationType(value);
    if (value === 'hash') {
      form.setFieldValue('data.confirmation.fileText', undefined);
    }
  });

  form.watch('mode', ({ value }) => {
    form.setFieldValue('remoteMethodId', undefined);
    form.setFieldValue('conversionPairId', null);
    setConversionPairId(null);

    setMethodMode(value);
    if (value !== 'custom') form.setFieldValue('useBlockchain', false);
  });

  form.watch('conversionPairId', ({ value }) => {
    form.setFieldValue('remoteMethodId', undefined);
    setConversionPairId(value);
  });

  const dataOptions = form.getValues().data?.step1.options;

  const handleSubmit = async ({ useBlockchain, ...values }: FormData) => {
    const params: TPaymentMethodUpdateParams = {
      ...values,
      remoteMethodId: (isPaymentMethodWithRemoteMethod(values.mode) && values.remoteMethodId) ? Number(values.remoteMethodId) : undefined,
      conversionPairId: values.conversionPairId ? Number(values.conversionPairId) : null,
      data: isPaymentMethodWithCustomFlow(values.mode) ? {
        ...values.data,
        blockchain: values.mode === 'custom' && paymentMethod.currency === 'USDT' && values.data.blockchain
          ? { ...values.data.blockchain, enabled: useBlockchain }
          : null,
      } : paymentMethod.data?.blockchain ? { ...values.data, blockchain: null } : undefined,
    };

    // console.log('edit method params: ', params);
    await updatePaymentMethod(params);
    onClose?.();
  };

  useShowResponseStatus({
    isSuccess,
    successMessage: 'Платёжный метод успешно обновлён',
    error: updateError,
  });

  return {
    form,
    isLoading,
    remoteMethods,
    isRemoteMethodsLoading,
    conversionPairs: avalableConversionPairs,
    isConversionPairsLoading,
    dataOptions,
    methodMode,
    confirmationType,
    useBlockchain,
    handleSubmit,
  };
};
